import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  Timestamp, 
  increment,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { app } from '../firebase';

const db = getFirestore();
const auth = getAuth();

// Collection names
const ANALYTICS_COLLECTION = 'analytics_v2';
const SESSIONS_COLLECTION = 'sessions_v2';
const PAGE_VIEWS_COLLECTION = 'pageViews_v2';
const VISITORS_COLLECTION = 'visitors';
const REAL_TIME_STATS = 'realTimeStats';

// Event types
export const EVENT_TYPES = {
  PAGE_VIEW: 'page_view',
  USER_SIGNUP: 'user_signup',
  USER_LOGIN: 'user_login',
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
  REGISTRY_ITEM_ADDED: 'registry_item_added',
  REGISTRY_ITEM_VIEWED: 'registry_item_viewed',
  REGISTRY_ITEM_PURCHASED: 'registry_item_purchased',
};

// Session configuration
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const HEARTBEAT_INTERVAL = 30 * 1000; // 30 seconds

// Global state
let currentSession = null;
let visitorFingerprint = null;
let sessionStartTime = null;
let lastActivityTime = null;
let pageViewCount = 0;
let currentUser = null;
let currentUserCustomUID = null; // Store the custom G-format UID
let heartbeatInterval = null;
let realTimeListeners = [];

// Get visitor's IP address and create fingerprint
const getVisitorIP = async () => {
  try {
    // Try multiple IP services for reliability
    const ipServices = [
      'https://api.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://api.ip.sb/jsonip'
    ];
    
    for (const service of ipServices) {
      try {
        const response = await fetch(service, { timeout: 5000 });
        if (response.ok) {
          const data = await response.json();
          return data.ip || data.query || null;
        }
      } catch (e) {
        
        continue;
      }
    }
    
    return null;
  } catch (error) {
    
    return null;
  }
};

// Get user's custom UID (G-format) from their email
const getUserCustomUID = async (user) => {
  if (!user || !user.email) return null;
  
  try {
    const userDocRef = doc(db, 'users', user.email);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      return userData.uid || null;
    }
  } catch (error) {
    
  }
  
  return null;
};

// Create a comprehensive visitor fingerprint
const createVisitorFingerprint = async () => {
  try {
    const fingerprint = {
      timestamp: new Date().toISOString(),
      ip: await getVisitorIP(),
      userAgent: navigator?.userAgent || 'unknown',
      language: navigator?.language || 'unknown',
      platform: navigator?.platform || 'unknown',
      screen: {
        width: screen?.width || 0,
        height: screen?.height || 0,
        colorDepth: screen?.colorDepth || 0,
      },
      timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'unknown',
      cookieEnabled: navigator?.cookieEnabled || false,
      doNotTrack: navigator?.doNotTrack || false,
    };
    
    // Create a hash for the fingerprint
    const fingerprintString = JSON.stringify(fingerprint);
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(fingerprintString));
    const hashArray = Array.from(new Uint8Array(hash));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return {
      id: hashHex.substring(0, 16), // Use first 16 chars as ID
      ...fingerprint
    };
  } catch (error) {
    
    // Fallback fingerprint
    return {
      id: 'fallback_' + Date.now(),
      timestamp: new Date().toISOString(),
      ip: null,
      userAgent: navigator?.userAgent || 'unknown',
      error: error.message
    };
  }
};

// Store or update visitor information
const trackVisitor = async (fingerprint) => {
  if (!fingerprint) return null;
  
  try {
    const visitorRef = doc(db, VISITORS_COLLECTION, fingerprint.id);
    const visitorSnap = await getDoc(visitorRef);
    
    if (visitorSnap.exists()) {
      // Update existing visitor
      await updateDoc(visitorRef, {
        lastSeen: Timestamp.now(),
        visitCount: increment(1),
        lastIP: fingerprint.ip,
        lastUserAgent: fingerprint.userAgent
      });
    } else {
      // Create new visitor
      await setDoc(visitorRef, {
        ...fingerprint,
        firstSeen: Timestamp.now(),
        lastSeen: Timestamp.now(),
        visitCount: 1,
        createdAt: Timestamp.now()
      });
    }
    
    return fingerprint.id;
  } catch (error) {
    
    return null;
  }
};

// Initialize real-time analytics
export const initAnalytics = async () => {
  try {
    
    
    // Create visitor fingerprint
    visitorFingerprint = await createVisitorFingerprint();
    
    
    // Track visitor
    await trackVisitor(visitorFingerprint);
    
    // Listen for auth state changes
    onAuthStateChanged(auth, async (user) => {
      try {
        currentUser = user;
        
        if (user) {
          
          // Fetch custom UID for this user
          currentUserCustomUID = await getUserCustomUID(user);
          
          
          // Use custom UID if available, fallback to Firebase UID
          const userIdForSession = currentUserCustomUID || user.uid;
          await startSession(userIdForSession);
        } else {
          
          currentUserCustomUID = null;
          await startSession(null);
        }
      } catch (e) {
        
      }
    });
    
    // Set up activity tracking
    if (typeof window !== 'undefined') {
      const activityEvents = ['click', 'keypress', 'scroll', 'mousemove', 'touchstart'];
      activityEvents.forEach(event => {
        window.addEventListener(event, updateActivity, { passive: true });
      });
      
      // Handle page unload
      window.addEventListener('beforeunload', async () => {
        await endSession();
        stopHeartbeat();
      });
      
      // Handle page visibility changes
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // Page is hidden, pause session
          pauseSession();
        } else {
          // Page is visible, resume session
          resumeSession();
        }
      });
    }
    
    // Start heartbeat for real-time updates
    startHeartbeat();
    
    // Initialize real-time listeners
    initRealTimeListeners();
    
    
  } catch (error) {
    
  }
};

// Start a new session with improved tracking
const startSession = async (userId) => {
  try {
    // End any existing session first
    if (currentSession) {
      await endSession();
    }
    
    sessionStartTime = new Date();
    lastActivityTime = sessionStartTime;
    pageViewCount = 0;
    
    // Create session document
    const sessionDoc = {
      userId: userId || 'anonymous',
      visitorId: visitorFingerprint?.id || 'unknown',
      startTime: Timestamp.fromDate(sessionStartTime),
      endTime: null,
      duration: 0,
      pageViews: 0,
      pages: [],
      ip: visitorFingerprint?.ip || null,
      userAgent: visitorFingerprint?.userAgent || 'unknown',
      deviceType: getDeviceType(),
      isActive: true,
      lastActivity: Timestamp.fromDate(sessionStartTime)
    };
    
    // Add session to Firestore
    const sessionRef = await addDoc(collection(db, SESSIONS_COLLECTION), sessionDoc);
    
    currentSession = {
      id: sessionRef.id,
      ...sessionDoc,
      startTime: sessionStartTime // Keep as Date for local operations
    };
    
    // Update real-time active sessions count
    await updateRealTimeStats('activeSessions', 1);
    
    // Log session start
    await logAnalyticsEvent(EVENT_TYPES.SESSION_START, {
      sessionId: currentSession.id,
      userId: userId || 'anonymous',
      visitorId: visitorFingerprint?.id
    });
    
    return currentSession.id;
  } catch (error) {
    
    return null;
  }
};

// End session with proper cleanup
const endSession = async () => {
  if (!currentSession) return;
  
  try {
    // Double-check to prevent race conditions
    if (!currentSession || !currentSession.id) return;
    
    // Capture session data before any async operations to prevent race conditions
    const sessionId = currentSession.id;
    const userId = currentSession.userId;
    const sessionPages = currentSession.pages || [];
    
    const endTime = new Date();
    const duration = endTime - sessionStartTime;
    
    // Update session in Firestore
    await updateDoc(doc(db, SESSIONS_COLLECTION, sessionId), {
      endTime: Timestamp.fromDate(endTime),
      duration: duration,
      pageViews: pageViewCount,
      pages: sessionPages,
      isActive: false
    });
    
    // Update real-time stats
    await updateRealTimeStats('activeSessions', -1);
    
    // Log session end
    await logAnalyticsEvent(EVENT_TYPES.SESSION_END, {
      sessionId: sessionId,
      userId: userId,
      duration: duration,
      pageViews: pageViewCount
    });
    
    
    
    // Reset session data
    currentSession = null;
    sessionStartTime = null;
    lastActivityTime = null;
    pageViewCount = 0;
  } catch (error) {
    
  }
};

// Pause session (when page is hidden)
const pauseSession = async () => {
  if (!currentSession) return;
  
  try {
    await updateDoc(doc(db, SESSIONS_COLLECTION, currentSession.id), {
      isActive: false,
      lastActivity: Timestamp.now()
    });
  } catch (error) {
    
  }
};

// Resume session (when page becomes visible again)
const resumeSession = async () => {
  if (!currentSession) return;
  
  try {
    updateActivity();
    await updateDoc(doc(db, SESSIONS_COLLECTION, currentSession.id), {
      isActive: true,
      lastActivity: Timestamp.now()
    });
  } catch (error) {
    
  }
};

// Update activity timestamp
const updateActivity = () => {
  lastActivityTime = new Date();
};

// Start heartbeat for real-time updates
const startHeartbeat = () => {
  if (heartbeatInterval) return;
  
  heartbeatInterval = setInterval(async () => {
    try {
      if (currentSession && lastActivityTime) {
        const now = new Date();
        const inactiveTime = now - lastActivityTime;
        
        if (inactiveTime < SESSION_TIMEOUT) {
          // Update session activity
          await updateDoc(doc(db, SESSIONS_COLLECTION, currentSession.id), {
            lastActivity: Timestamp.now()
          });
        } else {
          // Session has timed out
          await endSession();
        }
      }
    } catch (error) {
      
    }
  }, HEARTBEAT_INTERVAL);
};

// Stop heartbeat
const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

// Track page view with improved accuracy
export const trackPageView = async (pageName, pageParams = {}) => {
  try {
    if (!currentSession) {
      const userIdForSession = currentUserCustomUID || currentUser?.uid || null;
      await startSession(userIdForSession);
    }
    
    if (!currentSession) {
      
      return;
    }
    
    // Capture session data before async operations to prevent race conditions
    const sessionId = currentSession.id;
    const userId = currentSession.userId || 'anonymous';
    
    const safePageName = pageName || 'unknown';
    pageViewCount++;
    
    // Add page to session if not already included
    if (!currentSession.pages.includes(safePageName)) {
      currentSession.pages.push(safePageName);
    }
    
    // Create page view document
    const pageView = {
      sessionId: sessionId,
      userId: userId,
      visitorId: visitorFingerprint?.id || 'unknown',
      pageName: safePageName,
      timestamp: Timestamp.now(),
      params: pageParams || {},
      url: window?.location?.href || 'unknown',
      referrer: document?.referrer || 'direct'
    };
    
    // Add to Firestore
    await addDoc(collection(db, PAGE_VIEWS_COLLECTION), pageView);
    
    // Update real-time page views
    await updateRealTimeStats('pageViews', 1);
    
    // Update daily stats
    await updateDailyStats('pageViews', 1);
    
    // Log analytics event
    await logAnalyticsEvent(EVENT_TYPES.PAGE_VIEW, {
      pageName: safePageName,
      sessionId: sessionId,
      ...pageParams
    });
    
    updateActivity();
    
  } catch (error) {
    
  }
};

// Update real-time statistics
const updateRealTimeStats = async (metric, incrementValue) => {
  try {
    const statsRef = doc(db, REAL_TIME_STATS, 'current');
    const now = Timestamp.now();
    
    await updateDoc(statsRef, {
      [metric]: increment(incrementValue),
      lastUpdated: now
    }).catch(async () => {
      // Document doesn't exist, create it
      await setDoc(statsRef, {
        [metric]: incrementValue,
        lastUpdated: now,
        activeSessions: metric === 'activeSessions' ? incrementValue : 0,
        pageViews: metric === 'pageViews' ? incrementValue : 0,
        activeUsers: metric === 'activeUsers' ? incrementValue : 0
      });
    });
  } catch (error) {
    
  }
};

// Update daily statistics with deduplication
const updateDailyStats = async (metric, incrementValue) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateString = today.toISOString().split('T')[0];
    
    const statsRef = doc(db, 'dailyStats_v2', dateString);
    
    const updateData = {
      [metric]: increment(incrementValue),
      lastUpdated: Timestamp.now()
    };
    
    // Add unique visitor tracking for page views
    if (metric === 'pageViews' && visitorFingerprint?.id) {
      updateData[`visitors.${visitorFingerprint.id}`] = true;
    }
    
    await updateDoc(statsRef, updateData).catch(async () => {
      // Document doesn't exist, create it
      const initialData = {
        date: Timestamp.fromDate(today),
        pageViews: metric === 'pageViews' ? incrementValue : 0,
        activeUsers: [],
        newUsers: [],
        uniqueVisitors: [],
        visitors: {},
        lastUpdated: Timestamp.now()
      };
      
      if (metric === 'pageViews' && visitorFingerprint?.id) {
        initialData.visitors[visitorFingerprint.id] = true;
      }
      
      await setDoc(statsRef, initialData);
    });
  } catch (error) {
    
  }
};

// Initialize real-time listeners for live data
const initRealTimeListeners = () => {
  try {
    // Listen to real-time stats changes
    const statsRef = doc(db, REAL_TIME_STATS, 'current');
    const unsubscribeStats = onSnapshot(statsRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        // Emit custom event for components to listen to
        window.dispatchEvent(new CustomEvent('analyticsUpdate', {
          detail: {
            type: 'realTimeStats',
            data: data
          }
        }));
      }
    });
    
    realTimeListeners.push(unsubscribeStats);
  } catch (error) {
    
  }
};

// Get device type with more accuracy
const getDeviceType = () => {
  try {
    const userAgent = navigator?.userAgent || '';
    const width = window?.innerWidth || screen?.width || 0;
    
    // Mobile detection
    if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(userAgent)) {
      return 'mobile';
    }
    
    // Tablet detection
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent) || width >= 768) {
      return 'tablet';
    }
    
    return 'desktop';
  } catch (error) {
    
    return 'unknown';
  }
};

// Log analytics event (improved)
const logAnalyticsEvent = async (eventType, eventData) => {
  try {
    const event = {
      type: eventType,
      data: eventData || {},
      timestamp: Timestamp.now(),
      sessionId: currentSession?.id || 'no-session',
      userId: currentUser?.uid || 'anonymous',
      visitorId: visitorFingerprint?.id || 'unknown'
    };
    
    await addDoc(collection(db, ANALYTICS_COLLECTION), event);
  } catch (error) {
    
  }
};

// Improved analytics data retrieval
export const getAnalyticsData = {
  // Get real-time active users
  getRealTimeActiveUsers: () => {
    return new Promise((resolve) => {
      const q = query(
        collection(db, SESSIONS_COLLECTION),
        where('isActive', '==', true),
        where('lastActivity', '>', Timestamp.fromDate(new Date(Date.now() - SESSION_TIMEOUT)))
      );
      
      getDocs(q).then((snapshot) => {
        const uniqueUsers = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.userId && data.userId !== 'anonymous') {
            uniqueUsers.add(data.userId);
          } else if (data.visitorId) {
            uniqueUsers.add(data.visitorId);
          }
        });
        resolve(uniqueUsers.size);
      }).catch((error) => {
        
        resolve(0);
      });
    });
  },

  // Get total unique visitors (using fingerprinting)
  getTotalUniqueVisitors: async (startDate, endDate) => {
    try {
      const q = query(
        collection(db, VISITORS_COLLECTION),
        where('firstSeen', '>=', Timestamp.fromDate(startDate)),
        where('firstSeen', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      
      return 0;
    }
  },

  // Get accurate page views (deduplicated)
  getAccuratePageViews: async (startDate, endDate) => {
    try {
      const q = query(
        collection(db, PAGE_VIEWS_COLLECTION),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      
      return 0;
    }
  },

  // Get session-based metrics with better accuracy
  getSessionMetrics: async (startDate, endDate) => {
    try {
      const q = query(
        collection(db, SESSIONS_COLLECTION),
        where('startTime', '>=', Timestamp.fromDate(startDate)),
        where('startTime', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      
      let totalSessions = 0;
      let totalDuration = 0;
      let completedSessions = 0;
      let bounceSessions = 0;
      const uniqueUsers = new Set();
      const uniqueVisitors = new Set();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        totalSessions++;
        
        if (data.userId && data.userId !== 'anonymous') {
          uniqueUsers.add(data.userId);
        }
        
        if (data.visitorId) {
          uniqueVisitors.add(data.visitorId);
        }
        
        if (data.endTime) {
          completedSessions++;
          totalDuration += data.duration || 0;
          
          if ((data.pageViews || 0) <= 1) {
            bounceSessions++;
          }
        }
      });
      
      return {
        totalSessions,
        uniqueUsers: uniqueUsers.size,
        uniqueVisitors: uniqueVisitors.size,
        avgDuration: completedSessions > 0 ? Math.round(totalDuration / completedSessions) : 0,
        bounceRate: completedSessions > 0 ? Math.round((bounceSessions / completedSessions) * 100) : 0
      };
    } catch (error) {
      
      return {
        totalSessions: 0,
        uniqueUsers: 0,
        uniqueVisitors: 0,
        avgDuration: 0,
        bounceRate: 0
      };
    }
  },

  // Get popular pages with accurate view counts
  getPopularPages: async (startDate, endDate, limit = 10) => {
    try {
      const q = query(
        collection(db, PAGE_VIEWS_COLLECTION),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      const pageStats = {};
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        let pageName = data.pageName || 'Unknown';
        
        // Normalize page names to prevent duplicates - handle ALL variations
        // Convert specific user pages to generic page types for aggregation
        if (pageName === 'Hub' || pageName.startsWith('Hub-')) {
          pageName = 'Hub'; // Consolidate ALL hub variations (Hub, Hub-G88336, Hub-Anonymous, etc.)
        } else if (pageName === 'Registry' || pageName.startsWith('Registry-')) {
          pageName = 'Registry'; // Consolidate all registry pages
        } else if (pageName === 'Events' || pageName.startsWith('Events-')) {
          pageName = 'Events'; // Consolidate all events pages
        } else if (pageName === 'Admin' || pageName.startsWith('Admin-')) {
          pageName = 'Admin'; // Consolidate admin pages
        } else if (pageName === 'Profile' || pageName.startsWith('Profile-')) {
          pageName = 'Profile'; // Consolidate profile pages
        }
        
        if (!pageStats[pageName]) {
          pageStats[pageName] = {
            name: pageName,
            views: 0,
            uniqueVisitors: new Set()
          };
        }
        
        pageStats[pageName].views++;
        if (data.visitorId) {
          pageStats[pageName].uniqueVisitors.add(data.visitorId);
        }
      });
      
      // Convert to array and sort
      const popularPages = Object.values(pageStats)
        .map(page => ({
          ...page,
          uniqueVisitors: page.uniqueVisitors.size
        }))
        .sort((a, b) => b.views - a.views)
        .slice(0, limit);
      
      return popularPages;
    } catch (error) {
      
      return [];
    }
  },

  // Get most frequently added registry items across all users
  getPopularRegistryItems: async (startDate, endDate) => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const itemFrequency = new Map();
      
      // Iterate through all users
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const registryItems = userData.pages?.registryPageConfig?.items || [];
        
        // For each registry item
        for (const item of registryItems) {
          // Create a unique identifier for the item
          const itemKey = `${item.name}-${item.url}`;
          
          if (!itemFrequency.has(itemKey)) {
            itemFrequency.set(itemKey, {
              id: itemKey,
              name: item.name,
              category: item.category || 'Uncategorized',
              subCategory: item.subCategory || 'Uncategorized',
              url: item.url,
              image: item.image,
              price: item.price,
              frequency: 1,
              views: 0, // Will be populated from analytics events if available
              purchaseCount: item.purchased ? 1 : 0,
              lastAdded: item.addedAt || new Date(),
              addedByUsers: new Set([userDoc.id])
            });
          } else {
            const currentItem = itemFrequency.get(itemKey);
            currentItem.frequency += 1;
            currentItem.addedByUsers.add(userDoc.id);
            
            // Update purchase count
            if (item.purchased) {
              currentItem.purchaseCount += 1;
            }
            
            // Update lastAdded if more recent
            if (item.addedAt && (!currentItem.lastAdded || new Date(item.addedAt) > new Date(currentItem.lastAdded))) {
              currentItem.lastAdded = item.addedAt;
            }
          }
        }
      }
      
      // Convert to array and process data
      const popularItems = Array.from(itemFrequency.values())
        .map(item => ({
          ...item,
          addedByUsers: item.addedByUsers.size, // Convert Set to count
          purchaseRate: item.purchaseCount / item.frequency
        }))
        .filter(item => {
          // Filter by date range if item has addedAt timestamp
          if (item.lastAdded && startDate && endDate) {
            const itemDate = new Date(item.lastAdded);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return itemDate >= start && itemDate <= end;
          }
          return true; // Include items without timestamp
        })
        .sort((a, b) => {
          // Primary sort by frequency (how many users have this item)
          if (b.frequency !== a.frequency) {
            return b.frequency - a.frequency;
          }
          // Secondary sort by purchase rate
          if (b.purchaseRate !== a.purchaseRate) {
            return b.purchaseRate - a.purchaseRate;
          }
          // Tertiary sort by recency
          return new Date(b.lastAdded) - new Date(a.lastAdded);
        })
        .slice(0, 10); // Return top 10 items
      
      
      return popularItems;
    } catch (error) {
      
      return [];
    }
  }
};

// Cleanup function
export const cleanup = () => {
  try {
    stopHeartbeat();
    realTimeListeners.forEach(unsubscribe => unsubscribe());
    realTimeListeners = [];
  } catch (error) {
    
  }
};

// Track user signup
export const trackUserSignup = async (userId, userData) => {
  try {
    await logAnalyticsEvent(EVENT_TYPES.USER_SIGNUP, {
      userId,
      email: userData?.email || 'unknown'
    });
    
    // Update daily new users
    await updateDailyStats('newUsers', 1);
    
    
  } catch (error) {
    
  }
};

// Track registry item events (added, viewed, purchased)
export const trackRegistryItem = async (eventType, itemData, userId = null) => {
  try {
    // Validate inputs
    if (!eventType) {
      
      return;
    }
    
    const safeItemData = itemData || {
      id: 'unknown',
      name: 'Unknown Item',
      category: 'Unknown'
    };
    
    // Use current user if no userId provided
    const effectiveUserId = userId || currentUser?.uid || currentUserCustomUID || 'anonymous';
    
    // Prepare event data
    const eventData = {
      itemId: safeItemData.id || 'unknown',
      itemName: safeItemData.name || 'Unknown Item',
      itemCategory: safeItemData.category || 'Unknown',
      itemSubCategory: safeItemData.subCategory || 'Unknown',
      itemPrice: safeItemData.price || 0,
      itemUrl: safeItemData.url || '',
      userId: effectiveUserId,
      timestamp: new Date().toISOString()
    };
    
    // Log to Firebase Analytics
    await logAnalyticsEvent(eventType, eventData);
    
    // Log to Firestore analytics collection
    if (currentSession?.id && !currentSession.id.startsWith('minimal-') && !currentSession.id.startsWith('fallback-')) {
      const analytics = collection(db, 'analytics');
      await addDoc(analytics, {
        sessionId: currentSession.id,
        userId: effectiveUserId,
        eventType: eventType,
        eventData: eventData,
        timestamp: Timestamp.fromDate(new Date()),
        source: 'improved_analytics'
      });
    }
    
    
    
    // Update daily stats for registry item additions
    if (eventType === EVENT_TYPES.REGISTRY_ITEM_ADDED) {
      await updateDailyStats('registryItemsAdded', 1);
    }
    
    updateActivity();
  } catch (error) {
    
  }
};

export default {
  initAnalytics,
  trackPageView,
  trackUserSignup,
  trackRegistryItem,
  getAnalyticsData,
  cleanup,
  EVENT_TYPES
};